import { InternalEventStatus } from "@/lib/shared/enums";

const TEST_EVENT_NAME_REGEX = /\b(?:test(?:s|ing)?|(?:amy|anna|emily|hilary|nino|vin)(?:'s)?)\b/i;

/** The shared demo event id used across the customer portal, payments, and create-event flows. */
export const DEMO_EVENT_ID = 131;

type TestEventInput = {
  event_id?: number | null;
  name?: string | null;
  internal_status?: string | null;
};

/**
 * Returns true if an event NAME looks like a test/dev event — a whole-word match
 * on "test"/"testing" or a Nowadays dev first name (amy, anna, emily, hilary,
 * nino, vin). Word-boundary anchored so "Annapolis"/"Annual"/"vintage" don't trip.
 *
 * This is the single source of truth for the name heuristic, shared by both the
 * read-time {@link isTestEvent} check and the write-time auto-classification that
 * stamps `internal_status = 'test'` at event creation.
 *
 * @param name The event name (nullable).
 * @returns true when the name matches the test/dev-name heuristic.
 */
export function matchesTestEventName(name: string | null | undefined): boolean {
  return !!name && TEST_EVENT_NAME_REGEX.test(name);
}

/**
 * The `events` insert fields that auto-classify an obviously-test event by name. Spread into an
 * event insert row at every creation site: a name that trips {@link matchesTestEventName} stamps
 * `internal_status = 'test'` (so its RFP sends are suppressed); a non-matching name contributes no
 * key, leaving the column at its schema default (`active`).
 *
 * @param name The event name being inserted.
 * @returns `{ internal_status: 'test' }` when the name matches, else `{}`.
 */
export function testEventInsertFields(name: string | null | undefined): { internal_status?: InternalEventStatus.TEST } {
  return matchesTestEventName(name) ? { internal_status: InternalEventStatus.TEST } : {};
}

/**
 * Returns true if the event should be excluded from real-world side effects
 * (RFP emails, account-manager Slack pings). Trips on the structural
 * `internal_status` flag, the hardcoded demo event id, or a dev-name match.
 *
 * The name regex is the safety net for admins who forget to set the flag.
 *
 * @param event Event with at least one of `internal_status`, `event_id`, `name`.
 * @returns true if the event is a test/dev event.
 */
export function isTestEvent(event: TestEventInput): boolean {
  if (event.internal_status === InternalEventStatus.TEST) return true;
  if (event.event_id === DEMO_EVENT_ID) return true;
  if (matchesTestEventName(event.name)) return true;
  return false;
}
